import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LOGO_URL, BRANCH_EMBLEMS, NO_BAD_APPLES_BADGE } from "@/lib/constants";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { AnimatedCounter } from "@/components/AnimatedCounter";
import { Search, ShieldCheck, HeartHandshake, MapPin, Star, Lock, BadgeCheck, Trophy, Footprints } from "lucide-react";

export default function Landing() {
  const [verifiedPros, setVerifiedPros] = useState(null);
  const [spotless, setSpotless] = useState({ leaders: [], month: "" });
  const [gallery, setGallery] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [winners, setWinners] = useState([]);
  useEffect(() => {
    api.get("/stats/trust").then(({ data }) => setVerifiedPros(data.verified_pros)).catch(() => {});
    api.get("/stats/spotless").then(({ data }) => setSpotless(data)).catch(() => {});
    api.get("/gallery-highlights").then(({ data }) => setGallery(data.items || [])).catch(() => {});
    api.get("/featured-pro").then(({ data }) => setFeatured(data.featured)).catch(() => {});
    api.get("/featured-winners").then(({ data }) => setWinners(data.winners || [])).catch(() => {});
  }, []);
  return (
    <div className="overflow-hidden">
      {/* sliding logo band */}
      <div className="relative h-56 sm:h-72 bg-slate-900 border-b border-slate-800 flex items-center overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, #9b111e 0, transparent 40%), radial-gradient(circle at 80% 70%, #64748b 0, transparent 40%)",
          }}
        />
        <motion.img
          src={LOGO_URL}
          alt="Care-4U.net"
          data-testid="landing-sliding-logo"
          className="h-44 sm:h-60 object-contain drop-shadow-2xl"
          initial={{ x: "-40vw" }}
          animate={{ x: "110vw" }}
          transition={{ repeat: Infinity, duration: 26, ease: "linear" }}
        />
      </div>

      {/* hero */}
      <section className="max-w-7xl mx-auto px-4 py-16 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <p className="text-sm uppercase tracking-[0.25em] font-bold text-slate-500 mb-4">
            Mobile professionals, brought to you
          </p>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tighter text-slate-900">
            The people who <span className="text-[#9b111e]">come to you</span> — for anything you need.
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
            From haircuts and physical therapy to tennis lessons, companionship and home services —
            find friendly, vetted mobile professionals near you. Transparent pricing, honest 5% platform fee.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/search" data-testid="hero-find-btn" className="btn-red-3d flex items-center gap-2">
              <Search className="w-5 h-5" /> Find Services
            </Link>
            <Link to="/auth?role=professional" data-testid="hero-join-btn" className="btn-silver-3d">
              <span className="text-engraved">Apply Now</span>
            </Link>
          </div>
          {verifiedPros > 0 && (
            <p className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-slate-700" data-testid="trust-row">
              <BadgeCheck className="w-5 h-5 text-green-600 flex-shrink-0" />
              <span><span className="text-[#9b111e]">{verifiedPros}</span> pro{verifiedPros === 1 ? "" : "s"} verified by Care-4U</span>
            </p>
          )}
          <div className="mt-6 inline-flex items-center gap-3 rounded-full bg-[#9b111e] text-white pl-4 pr-5 py-2 shadow-lg" data-testid="hero-military-discount">
            <Star className="w-5 h-5 fill-white text-white flex-shrink-0" />
            <span className="text-sm sm:text-base font-bold">
              Veterans &amp; Active Military save <span className="text-yellow-300">10%</span> — verified with military ID
            </span>
          </div>
          <p className="mt-4 text-sm text-slate-600 flex items-center gap-2" data-testid="hero-home-pledge">
            <HeartHandshake className="w-4 h-4 text-[#9b111e] flex-shrink-0" />
            <span>Every client pledges to <b>be home for every booking</b> and keep a <b>neat, tidy, safe space</b> — welcoming their pro warmly at the door by nickname.</span>
          </p>
          <p className="mt-3 text-sm text-slate-600 flex items-center gap-2" data-testid="hero-footcovers-pledge">
            <Footprints className="w-4 h-4 text-[#9b111e] flex-shrink-0" />
            <span>Every pro pledges to <b>always wear foot covers</b> — keeping your home spotless on every visit.</span>
          </p>
          <div className="mt-6 rounded-xl border-2 border-[#9b111e] bg-white p-4" data-testid="hero-hiring-statement">
            <p className="text-sm sm:text-base text-slate-800 font-semibold leading-relaxed">
              I'm <span className="text-[#9b111e] font-black">always seeking fresh talent</span> to serve those in need,
              so if you have at least <b>4 years of experience</b> (<i>work / school / military</i>) please{" "}
              <span className="text-[#9b111e] font-black">APPLY NOW!!!!!!!</span>
            </p>
            <Link to="/auth?role=professional" data-testid="hero-hiring-apply-btn" className="btn-red-3d mt-3 inline-flex">
              Apply to Join
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-6 text-sm text-slate-500">
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#9b111e]" /> Location-based search</span>
            <span className="flex items-center gap-2"><Star className="w-4 h-4 text-[#9b111e]" /> 1–10 ratings & badges</span>
            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#9b111e]" /> Vetted professionals</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {[
            { icon: HeartHandshake, count: 200, suffix: "+", label: "Services", d: "Care, lessons, parties, home & more" },
            { icon: Star, t: "Earn Badges", d: "Bronze → God-Level tiers" },
            { icon: ShieldCheck, t: "Veteran Founded", d: "We support all who served" },
          ].map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="card-metal p-6"
            >
              <c.icon className="w-8 h-8 text-[#9b111e] mb-3" />
              <h3 className="font-heading font-black text-lg text-slate-900" data-testid={`stat-card-${i}`}>
                {c.count != null
                  ? <><AnimatedCounter value={c.count} suffix={c.suffix} /> {c.label}</>
                  : c.t}
              </h3>
              <p className="text-sm text-slate-500 mt-1">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Real Results photo strip */}
      {gallery.length > 0 && (
        <section className="bg-slate-900 py-14 overflow-hidden" data-testid="landing-real-results">
          <div className="max-w-6xl mx-auto px-4 text-center mb-8">
            <span className="inline-flex items-center gap-2 text-[#e05260] font-heading font-black text-sm uppercase tracking-widest">
              <Star className="w-4 h-4 fill-current" /> Real Results
            </span>
            <h2 className="font-heading font-black text-white mt-2 text-base md:text-lg">
              Fresh cuts, sparkling homes — real photos from real client reviews
            </h2>
          </div>
          <div className="relative">
            <div className="flex gap-4 w-max gallery-marquee" style={{ animationDuration: `${Math.max(24, gallery.length * 5)}s` }}>
              {[...gallery, ...gallery].map((g, n) => (
                <Link key={`${g.review_id}-${g.idx}-${n}`} to={`/provider/${g.provider_id}`}
                  className="relative flex-shrink-0 w-52 h-40 rounded-xl overflow-hidden border border-slate-700 group"
                  data-testid={n < gallery.length ? `landing-gallery-photo-${g.review_id}-${g.idx}` : undefined}>
                  <img src={`${API}/reviews/${g.review_id}/photo/${g.idx}`} alt={`${g.service} result`} loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <span className="absolute top-2 right-2 text-[11px] font-black text-white bg-[#9b111e]/90 rounded-full px-2 py-0.5">
                    ★ {g.rating}/10
                  </span>
                  <span className="absolute bottom-0 inset-x-0 text-[11px] text-white bg-gradient-to-t from-black/80 to-transparent px-2.5 pt-5 pb-2 truncate">
                    {g.service} · by {g.provider_name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Pro of the week */}
      {featured && (
        <section className="bg-white py-16" data-testid="featured-pro-section">
          <div className="max-w-5xl mx-auto px-4">
            <span className="inline-flex items-center gap-2 text-[#9b111e] font-heading font-black text-sm uppercase tracking-widest">
              <Trophy className="w-4 h-4" /> Featured Pro of the Week
            </span>
            <div className="mt-4 grid md:grid-cols-2 gap-0 rounded-2xl overflow-hidden border-2 border-[#9b111e] shadow-xl">
              {featured.photo ? (
                <img src={`${API}/reviews/${featured.photo.review_id}/photo/${featured.photo.idx}`}
                  alt={`${featured.name}'s best work`} loading="lazy"
                  className="w-full h-64 md:h-full object-cover" data-testid="featured-pro-photo" />
              ) : (
                <div className="w-full h-64 md:h-full bg-slate-900 flex items-center justify-center">
                  <Star className="w-16 h-16 text-[#9b111e] fill-current" />
                </div>
              )}
              <div className="p-7 bg-slate-50">
                <h2 className="font-heading font-black text-slate-900 text-base md:text-lg" data-testid="featured-pro-name">
                  {featured.name}
                </h2>
                <p className="text-sm text-slate-500">{featured.city}{featured.state ? `, ${featured.state}` : ""}</p>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className="inline-flex items-center gap-1 text-sm font-black text-white bg-[#9b111e] rounded-full px-3 py-1" data-testid="featured-pro-rating">
                    <Star className="w-3.5 h-3.5 fill-current" /> {featured.week_rating}/10 this week
                  </span>
                  <span className="text-xs font-semibold text-slate-500">{featured.review_count} lifetime reviews · avg {featured.avg_rating}/10</span>
                </div>
                {featured.services?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {featured.services.map((sv) => (
                      <span key={sv} className="text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-full px-2.5 py-1">{sv}</span>
                    ))}
                  </div>
                )}
                {featured.quote?.comment && (
                  <blockquote className="mt-4 text-sm text-slate-700 italic border-l-4 border-[#9b111e] pl-3">
                    "{featured.quote.comment}"
                    <span className="not-italic text-slate-400"> — {featured.quote.client_name}, {featured.quote.rating}/10</span>
                  </blockquote>
                )}
                <Link to={`/provider/${featured.provider_id}`} className="btn-red-3d inline-flex mt-5" data-testid="featured-pro-book-btn">
                  Book {featured.name?.split(" ")[0]}
                </Link>
              </div>
            </div>
            {winners.length > 0 && (
              <div className="mt-8" data-testid="winners-wall">
                <h3 className="font-heading font-black text-slate-700 text-sm uppercase tracking-widest mb-3">🏆 Past Winners Wall</h3>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {winners.map((w) => (
                    <Link key={w.id} to={`/provider/${w.provider_id}`}
                      className="flex-shrink-0 rounded-xl border border-slate-200 bg-slate-50 hover:border-[#9b111e] transition-colors px-4 py-3 min-w-[180px]"
                      data-testid={`winner-card-${w.id}`}>
                      <p className="text-[11px] font-bold text-slate-400">Week of {new Date(w.week_start + "T00:00:00").toLocaleDateString()}</p>
                      <p className="font-heading font-black text-slate-900 text-sm mt-0.5 truncate">{w.name}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="inline-flex items-center gap-1 text-[11px] font-black text-white bg-[#9b111e] rounded-full px-2 py-0.5">
                          <Star className="w-3 h-3 fill-current" /> {w.week_rating}/10
                        </span>
                        {w.streak > 1 && (
                          <span className="text-[11px] font-black text-amber-700 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5" data-testid={`winner-streak-${w.id}`}>
                            🔥 {w.streak}-week streak
                          </span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Spotless Pros leaderboard */}
      {spotless.leaders && spotless.leaders.length > 0 && (
        <section className="bg-slate-50 py-16" data-testid="spotless-leaderboard-section">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center">
              <span className="inline-flex items-center gap-2 text-[#9b111e] font-heading font-black text-sm uppercase tracking-widest">
                <Trophy className="w-5 h-5" /> Spotless Pros
              </span>
              <h2 className="font-heading font-black text-3xl sm:text-4xl text-slate-900 mt-2">
                This Month's Cleanest Records
              </h2>
              <p className="text-slate-500 mt-2">
                Our top pros with a <b>spotless NO BAD APPLES record</b> — zero strikes, all smiles.
                {spotless.month ? ` · ${spotless.month}` : ""}
              </p>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {spotless.leaders.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className={`card-metal p-5 relative ${i < 3 ? "ring-2 ring-[#9b111e]/30" : ""}`}
                  data-testid={`spotless-pro-${p.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-heading font-black text-2xl ${i === 0 ? "text-yellow-500" : i === 1 ? "text-slate-400" : i === 2 ? "text-amber-700" : "text-slate-300"}`}>
                      {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-50 rounded-full px-2 py-1">
                      <BadgeCheck className="w-3.5 h-3.5" /> Spotless
                    </span>
                  </div>
                  <h3 className="font-heading font-black text-lg text-slate-900 mt-3">{p.name}</h3>
                  <p className="text-sm text-slate-500 flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> {p.city}{p.state ? `, ${p.state}` : ""}
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 font-heading font-black text-[#9b111e]">
                      <Star className="w-4 h-4 fill-[#9b111e]" /> {p.avg_rating}/10
                    </span>
                    <span className="text-xs font-bold text-slate-400 uppercase">{p.badge}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{p.review_count} review{p.review_count === 1 ? "" : "s"}
                    {p.great_this_month > 0 ? ` · ${p.great_this_month} great this month` : ""}</p>
                  <Link to={`/provider/${p.id}`} className="btn-silver-3d w-full mt-4 !py-2" data-testid={`spotless-view-${p.id}`}>
                    <span className="text-engraved text-sm">View Profile</span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* NO BAD APPLES guarantee */}
      <section className="bg-white py-16 border-y-4 border-[#9b111e]" data-testid="no-bad-apples-section">
        <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-10 items-center">
          <div className="flex justify-center">
            <img src={NO_BAD_APPLES_BADGE} alt="No Bad Apples Guarantee"
              className="w-64 h-64 object-contain drop-shadow-xl" data-testid="no-bad-apples-logo" />
          </div>
          <div>
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-[#9b111e]">Our "NO BAD APPLES" Guarantee</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Every Care-4U pro is held to a high standard — automatically. A rating under 8 or a no-show earns a
              strike, and <b>three strikes drops a pro to discounted Back-Up status</b> until they earn their way
              back with great reviews. Pros pledge to <b>always put your satisfaction first</b>.
            </p>
            <p className="mt-3 font-heading font-black text-xl text-slate-900">We have NO BAD APPLES. Guaranteed. 🍎🚫</p>
          </div>
        </div>
      </section>

      {/* veteran strip */}
      <section className="bg-slate-900 text-white py-14">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="font-heading font-black text-2xl sm:text-3xl">We Support Our Veterans</h2>
          <p className="text-slate-400 mt-3 max-w-2xl mx-auto">
            Proudly founded by a disabled Special-Operations Air-Force veteran. We honor all five
            branches of the United States Armed Forces.
          </p>
          <div className="mt-6 inline-block bg-[#9b111e] rounded-xl px-6 py-4" data-testid="veteran-strip-discount">
            <p className="font-heading font-black text-2xl">10% Military Discount ⭐</p>
            <p className="text-red-100 text-sm mt-1">
              Active-duty &amp; veterans save 10% on every booking — with quick verification of your military ID.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-6 sm:gap-10">
            {BRANCH_EMBLEMS.map((b) => (
              <div key={b.name} className="flex flex-col items-center gap-2" data-testid={`branch-${b.name.replace(/\s/g, "-").toLowerCase()}`}>
                <img src={b.url} alt={`U.S. ${b.name} emblem`}
                  className="h-24 w-24 sm:h-28 sm:w-28 object-contain rounded-full drop-shadow-[0_6px_14px_rgba(155,17,30,0.5)] transition-transform hover:scale-110" />
                <span className="text-xs font-heading font-black tracking-wide text-slate-300">U.S. {b.name}</span>
              </div>
            ))}
          </div>
          <Link to="/about" data-testid="landing-about-btn" className="btn-silver-3d inline-block mt-10">
            <span className="text-engraved">Our Story</span>
          </Link>
        </div>
      </section>

      <footer className="py-8 text-center text-sm text-slate-500 bg-slate-100 border-t border-slate-200">
        <p className="flex items-center justify-center gap-1.5 flex-wrap">
          <Lock className="w-4 h-4 text-green-600" /> All transactions secured with <b>256-bit SSL encryption</b> · Payments processed securely by Stripe
        </p>
        <p className="mt-2">© {new Date().getFullYear()} Care-4U.net · Transparent 5% platform fee · Built for those who serve others.</p>
      </footer>
    </div>
  );
}
